import { use, useEffect, useImperativeHandle, useRef, useState } from "react";
import { isArrayEmpty, isObjectNull } from "../../../../components/helpers/ObjectHelpers";
import moment from "moment/moment";
import { convertPilotAssignmentApiDtoToIdb, getPilotAssignmentStatusColor, getPilotAssignmentStatusIconColor, getPilotAssignmentUrl } from "../../pilot-assignment/helpers/PilotAssignmentHelpers";
import { PilotagePilotCardBody } from "../../components/PilotagesPageStates";
import PilotApiRepository from "../../../../repositories/api/PilotApiRepository";
import PilotageApiRepository from "../../../../repositories/api/PilotageApiRepository";
import PilotAssignmentIdbRepository from "../../../../repositories/idb/PilotAssignmentIdbRepository";
import { processPilotAssignment } from "../../pilot-assignment/helpers/PilotAssignmentProcessHelpers";
import AppInsightsService from "../../../../services/AppInsightsService";
import { useNavigate } from "react-router";
import FloatingButtonContainer from "../../../../components/layout/FloatingButtonContainer";
import FloatingActionButton from "../../../../components/layout/FloatingActionButton";
import { TrashIcon } from "../../../../components/layout/icons/Icons";
import { PilotAssignmentUpdatedDialog } from "../../pilot-assignment/components/dialogs/PilotAssignmentUpdatedDialog";
import { DialogConfirm } from "../../../../components/layout/dialogs/components/DialogConfirm";
import { DialogProperties } from '../../../../components/layout/dialogs/DialogProperties';
import PilotAssignmentService from "../../pilot-assignment/services/PilotAssignmentService";
import { SuspenseWithErrorBoundry } from "../../../../components/layout/suspense/SuspenseWithErrorBoundry";
import { SuspenseFallback } from "../../../../components/layout/suspense/SuspenseFallback";
import { ErrorBoundryFallback } from "../../../../components/layout/suspense/ErrorBoundryFallback";
import ContentGrid from "../../../../components/layout/ContentGrid";
import { CardList } from "../../../../components/layout/card/CardList";
import Spacer from "../../../../components/layout/Spacer";
import { useRefreshedData, useSuccessNotification, useWarningNotification } from "components/hooks/useAppStore";

export const PilotagesAssigned = () => {

    const [dataPromise, setDataPromise] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const refreshedData = useRefreshedData();
    const addSuccessNotification = useSuccessNotification();
    const addWarningNotification = useWarningNotification();

    const canDelete = !isArrayEmpty(selectedItems);

    const contentRef = useRef(null);

    const cleanupIdb = async (data) => {
        const idbPilotages =
            await PilotAssignmentIdbRepository.getAllActualAsync();
        if (isArrayEmpty(idbPilotages)) return;

        for (const idbPilotage of idbPilotages) {
            const dto = data.find(
                (x) =>
                    x.pilotagePilotId ===
                    idbPilotage.pilotagePilotId,
            );
            if (!isObjectNull(dto)) continue;
            await PilotAssignmentIdbRepository.deleteAsync(idbPilotage);
        }
    }

    const processData = async (dto, result) => {
        const pilotAssignment = convertPilotAssignmentApiDtoToIdb(dto);
        const idbPilotAssignment =
            await PilotAssignmentIdbRepository.getAsync(
                pilotAssignment.pilotagePilotId,
            );

        try {
            if (!isObjectNull(idbPilotAssignment)) {
                const processedPilotAssignment =
                    processPilotAssignment(
                        idbPilotAssignment,
                        pilotAssignment,
                    );
                await PilotAssignmentIdbRepository.setAsync(
                    processedPilotAssignment,
                );
                result.push(processedPilotAssignment);
            } else {
                await PilotAssignmentIdbRepository.setAsync(
                    pilotAssignment,
                );
                result.push(pilotAssignment);
            }
        } catch (e) {
            console.warn(e);
            AppInsightsService.trackException(e);
            const warning = `Kunne ikke behandle losoppdrag ${pilotAssignment.pilotage.pilotageShip.shipName} ${pilotAssignment.pilotage.pilotageNo}.`;
            if (!isObjectNull(idbPilotAssignment)) {
                addWarningNotification(warning);
                result.push(idbPilotAssignment);
            } else {
                result.push(pilotAssignment);
            }
        }
    }

    const fetchDataOffline = async () => {
        return await PilotAssignmentIdbRepository.getAllActualAsync();
    }

    const fetchDataOnline = async () => {
        let result = [];

        const response = await PilotApiRepository.getAssignedPilotagesAsync();

        if (response.ok) {
            const data = await response.json();

            if (!isArrayEmpty(data)) {
                for (const dto of data) {
                    await processData(dto, result);
                }
                await cleanupIdb(data);
            }
            else {
                await deleteIdbPilotagesAsync();
            }
        }
        else {
            result = fetchDataOffline();
        }

        return result;
    }

    const fetchData = async () => {
        await PilotAssignmentIdbRepository.cleanUpAsync();
        if (navigator.onLine) { return fetchDataOnline(); }
        else { return fetchDataOffline(); }

    }

    const initialize = () => {
        setDataPromise(fetchData());
    }

    useEffect(() => {
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!refreshedData) return;
        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshedData]);

    return (
        <>
            <ContentGrid title="Aktuelle losoppdrag" titleIcon="anchor" onPullToRefresh={initialize}>
                <SuspenseWithErrorBoundry
                    suspenseFallback={<SuspenseFallback />}
                    errorBoundryFallback={<ErrorBoundryFallback mode="text" text="Kunne ikke laste inn losoppdrag..." />}>
                    {
                        dataPromise &&
                        <PilotagesAssignedContent
                            ref={contentRef}
                            dataPromise={dataPromise}
                            setSelectedItems={setSelectedItems}
                            onDelete={onDelete}
                        />
                    }
                </SuspenseWithErrorBoundry>
            </ContentGrid>

            {
                (canDelete) &&
                <FloatingButtonContainer hasBottomNavigation={true}>
                    <FloatingActionButton
                        themeColor={canDelete ? "error" : "secondary"}
                        disabled={!canDelete}
                        svgIcon={TrashIcon}
                        onClick={onDeleteMultible}
                    />
                </FloatingButtonContainer>
            }

            {showConfirmDialog && (
                <DialogConfirm
                    properties={{
                        ...DialogProperties,
                        title: "Slett losoppdrag",
                        text: "Ønsker du å slette valgte losoppdrag?",
                        onClose: () => {
                            setShowConfirmDialog(false);
                        },
                        onClick: onConfirmDeleteCallback,
                    }}
                />
            )}
        </>
    )

    async function deleteIdbPilotagesAsync() {
        const idbPilotages = await PilotAssignmentIdbRepository.getAllActualAsync();
        if (isArrayEmpty(idbPilotages)) return;

        for (const idbPilotage of idbPilotages) {
            await PilotAssignmentIdbRepository.deleteAsync(idbPilotage);
        }
    }

    async function onDeleteMultible() {
        let hasChanges = false;

        for (const id of selectedItems) {
            const pilotAssignment = await PilotAssignmentIdbRepository.getAsync(id);
            const assignmentService = new PilotAssignmentService(pilotAssignment);
            hasChanges = assignmentService.isDirty;

            if (hasChanges) break;
        }

        if (hasChanges) {
            setShowConfirmDialog(true);
        } else {
            thenDelete(selectedItems);
        }
    }

    function onDelete(pilotAssignment) {
        const array = [pilotAssignment.pilotagePilotId];
        thenDelete(array);
    }

    async function thenDelete(ids) {
        if (isArrayEmpty(ids)) return;
        let isApprentice = false;

        for (const id of ids) {
            const pilotAssignment = await PilotAssignmentIdbRepository.getAsync(id);
            isApprentice = pilotAssignment.isApprentice;

            if (isObjectNull(pilotAssignment)) continue;
            if (isApprentice) {
                await PilotageApiRepository.removeSelfAssignedPilotageAsync(pilotAssignment.pilotagePilotId);
            } else {
                await PilotAssignmentIdbRepository.deleteAsync(pilotAssignment);
            }
        }
        if (isApprentice) 
            addSuccessNotification(`Du har fjernet deg selv fra oppdraget.`);

        setSelectedItems(prev =>
            prev.filter(id => !ids.includes(id))
        );
        contentRef.current.onDeleted(ids);
    }

    async function onConfirmDeleteCallback() {
        setShowConfirmDialog(false);
        thenDelete(selectedItems);
    }
}

const PilotagesAssignedContent = ({
    ref,
    dataPromise,
    setSelectedItems,
    onDelete
}) => {

    useImperativeHandle(ref, () => ({
        onDeleted(deletedIds) {
            listRef.current.onDeleted(deletedIds);
        }
    }));

    const data = use(dataPromise);

    const [showDialog, setShowDialog] = useState(false);
    const [pilotAssignment, setPilotAssignment] = useState(null);
    const [cards, setCards] = useState([]);

    const navigate = useNavigate();
    const listRef = useRef(null);

    useEffect(() => {
        const result = [];
        for (const pilotAssignment of data) {
            result.push(createListItem(pilotAssignment));
        }
        setCards(result);
    }, [data]);

    return (
        <>
            <CardList
                ref={listRef}
                source={cards}
                noContentMessage="Du har ingen tildelte losoppdrag"
            />
            <Spacer height={20} />
            {showDialog && (
                <PilotAssignmentUpdatedDialog
                    onClose={onDialogClick}
                    onClick={onDialogClick}
                />
            )}
        </>
    )

    function createListItem(pilotAssignment) {
        return {
            key: pilotAssignment.guid,
            id: pilotAssignment.pilotagePilotId,
            title: pilotAssignment.pilotage.pilotageShip.shipName,
            subTitle: moment(pilotAssignment.pilotage.pilotageDetail.fromTime).format('DD.MM HH:mm'),
            borderColor: getPilotAssignmentStatusColor(pilotAssignment),
            iconColor: getPilotAssignmentStatusIconColor(pilotAssignment),
            showIndicator: true,
            indicatorPrimaryColor: getPilotAssignmentStatusColor(pilotAssignment),
            onClick: () => onClick(pilotAssignment),
            canSelect: true,
            onSelect: (id, selected) => onToggleSelectItem(id, selected),
            canDelete: true,
            onDelete: () => onDelete(pilotAssignment),
            confirmDelete: isDirty(pilotAssignment),
            confirmDeleteTitle: "Slett losoppdrag",
            confirmDeleteText: "Skipperbeviset har lokale endringer. Ved sletting, så vil disse forsvinne. Ønsker du å slette valgte losoppdrag?",
            content: <PilotagePilotCardBody pilotagePilot={pilotAssignment} />
        };
    }

    function isDirty(pilotAssignment) {
        const assignmentService = new PilotAssignmentService(pilotAssignment);
        return assignmentService.isDirty;
    }

    function onToggleSelectItem(id, selected) {
        setSelectedItems((prevIds) => {
            const ids = [...prevIds];
            if (selected) {
                ids.push(id);
            } else {
                const index = ids.indexOf(id);
                if (index > -1) {
                    ids.splice(index, 1);
                }
            }
            return ids;
        });
    }

    function onClick(pilotAssignment) {
        if (pilotAssignment.wasServerUpdated === true) {
            setShowDialog(true);
            setPilotAssignment(pilotAssignment);
        } else {
            navigate(getPilotAssignmentUrl(pilotAssignment));
        }
    }

    async function onDialogClick() {
        const dto = await PilotAssignmentIdbRepository.getAsync(
            pilotAssignment.pilotagePilotId,
        );
        dto.wasServerUpdated = false;
        await PilotAssignmentIdbRepository.setAsync(dto);

        navigate(getPilotAssignmentUrl(dto));
    }
}
